import { createReducer, on } from '@ngrx/store';
import * as AuditActions from './audit.actions';
import { initialAuditsState, AuditsState } from './audit.model';
import { toListItem } from './audit-list-item.model';

/**
 * Audits Reducer
 * 
 * This pure function handles state transitions based on actions.
 * It NEVER mutates state - always returns a new state object.
 * 
 * This solves:
 * ✅ Race conditions - state updates are serialized
 * ✅ Predictable state - every action produces same result
 * ✅ Testability - pure function, easy to test
 */
export const auditsReducer = createReducer(
  initialAuditsState,

  // Load Audits (full – shows spinner, list hidden while loading)
  on(AuditActions.loadAudits, (state): AuditsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  // Refresh Audits (silent – list stays visible, only subtle indicator shown)
  on(AuditActions.refreshAudits, (state): AuditsState => ({
    ...state,
    backgroundRefresh: true,
    error: null,
  })),

  on(AuditActions.loadAuditsSuccess, (state, { audits }): AuditsState => ({
    ...state,
    audits,
    loading: false,
    backgroundRefresh: false,
    error: null,
    lastUpdated: new Date(),
  })),

  on(AuditActions.loadAuditsFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    backgroundRefresh: false,
    error,
  })),

  // Load Single Audit
  on(AuditActions.loadAudit, (state): AuditsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(AuditActions.loadAuditSuccess, (state, { audit }): AuditsState => ({
    ...state,
    selectedAudit: audit,
    loading: false,
    error: null,
    // Also update in list if it exists
    audits: state.audits.map((a) => (a.id === audit.id ? toListItem(audit) : a)),
  })),

  on(AuditActions.loadAuditFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  })),

  // Create Audit
  on(AuditActions.createAudit, (state): AuditsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(AuditActions.createAuditSuccess, (state, { audit }): AuditsState => ({
    ...state,
    audits: [toListItem(audit), ...state.audits],
    selectedAudit: audit,
    loading: false,
    error: null,
  })),

  on(AuditActions.createAuditFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  })),

  // Delete Audit
  on(AuditActions.deleteAudit, (state): AuditsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(AuditActions.deleteAuditSuccess, (state, { auditId }): AuditsState => ({
    ...state,
    audits: state.audits.filter((a) => a.id !== auditId),
    selectedAudit: state.selectedAudit?.id === auditId ? null : state.selectedAudit,
    loading: false,
    error: null,
  })),

  on(AuditActions.deleteAuditFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  })),

  // Clear Selected
  on(AuditActions.clearSelectedAudit, (state): AuditsState => ({
    ...state,
    selectedAudit: null,
  })),

  // Update Audit Status (kein loading-Spinner – nur der betroffene Eintrag wird gepatcht)
  on(AuditActions.updateAuditStatus, (state): AuditsState => ({
    ...state,
    error: null,
  })),

  on(AuditActions.updateAuditStatusSuccess, (state, { auditId, status }): AuditsState => ({
    ...state,
    audits: state.audits.map(audit =>
      audit.id === auditId ? { ...audit, status } : audit
    ),
    selectedAudit: state.selectedAudit?.id === auditId 
      ? { ...state.selectedAudit, status } 
      : state.selectedAudit,
    loading: false,
    error: null,
  })),

  on(AuditActions.updateAuditStatusFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  })),

  // Bulk Update Audit Status (kein loading-Spinner – nur betroffene Einträge werden gepatcht)
  on(AuditActions.bulkUpdateAuditStatus, (state): AuditsState => ({
    ...state,
    error: null,
  })),

  on(AuditActions.bulkUpdateAuditStatusSuccess, (state, { auditIds, status }): AuditsState => ({
    ...state,
    audits: state.audits.map(audit =>
      auditIds.includes(audit.id!) ? { ...audit, status } : audit
    ),
    loading: false,
    error: null,
  })),

  on(AuditActions.bulkUpdateAuditStatusFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  })),

  // Bulk Delete Audits
  on(AuditActions.bulkDeleteAudits, (state): AuditsState => ({
    ...state,
    error: null,
  })),

  on(AuditActions.bulkDeleteAuditsSuccess, (state, { auditIds }): AuditsState => ({
    ...state,
    audits: state.audits.filter(audit => !auditIds.includes(audit.id!)),
    loading: false,
    error: null,
  })),

  on(AuditActions.bulkDeleteAuditsFailure, (state, { error }): AuditsState => ({
    ...state,
    loading: false,
    error,
  }))
);
